
import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import {
    Collapse, Chip, IconButton, Tooltip,
} from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import PhotoCamera from '@material-ui/icons/Link';
import Checkbox from '@material-ui/core/Checkbox';
import EnhancedTableHead from './EnhancedTableHead';
import enhancedTableColsProps from './EnhancedTable.cols.props';
import EnhancedTableToolbar from './EnhancedTableToolbar';
import translationPropType from '../../../proptypes/translationPropType';

const desc = (a, b, orderBy) => {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
};

const stableSort = (array, cmp) => {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = cmp(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map(el => el[0]);
};

const getSorting = (order, orderBy) => (order === 'desc' ? (a, b) => desc(a, b, orderBy) : (a, b) => -desc(a, b, orderBy));

const styles = theme => ({
    table: {
        minWidth: 200,
    },
    tableWrapper: {
        overflowX: 'auto',
    },
    collapseTable: {
        display: 'inline-block',
    },
    infoChip: {
        margin: theme.spacing(1),
        marginLeft: theme.spacing(3),
    },
});

const initialState = {
    order: 'desc',
    orderBy: '',
    selected: [],
    page: 0,
    rowsPerPage: 25,
    collapsed: false,
};

const perPageOptions = [10, 25, 50, 100, 200, 500, 1000];

const autoPageSize = count => perPageOptions.find(pageSize => pageSize > count) || 1000;

class EnhancedTable extends React.Component {
    constructor(props) {
        super(props);
        this.handleRequestSort = this.handleRequestSort.bind(this);
        this.handleSelectAllClick = this.handleSelectAllClick.bind(this);
        this.handleClick = this.handleClick.bind(this);
        this.handleChangePage = this.handleChangePage.bind(this);
        this.handleChangeRowsPerPage = this.handleChangeRowsPerPage.bind(this);
        this.handleSelectedAction = this.handleSelectedAction.bind(this);
        this.handleCollapse = this.handleCollapse.bind(this);
        this.isSelected = this.isSelected.bind(this);
        this.state = Object.assign(
            {},
            initialState,
            {
                orderBy: props.cols.find(col => col.defaultSorting).id,
                collapsed: props.initiallyCollapsed,
                rowsPerPage: props.fitInPage
                    ? autoPageSize(props.fitInPage)
                    : initialState.rowsPerPage,
            },
        );
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        const { rows, cols, initiallyCollapsed } = this.props;

        if (!_.isEqual(rows, nextProps.rows) || !_.isEqual(cols, nextProps.cols)) {
            this.setState(Object.assign(
                {},
                initialState,
                {
                    orderBy: cols.find(col => col.defaultSorting).id,
                    collapsed: initiallyCollapsed,
                },
            ));
        }
    }

    handleRequestSort(event, property) {
        const orderBy = property;
        let order = 'desc';

        const { orderBy: orderByState, order: orderState } = this.state;

        if (orderByState === property && orderState === 'desc') {
            order = 'asc';
        }

        this.setState({ order, orderBy });
    }

    handleSelectAllClick(event) {
        if (event.target.checked) {
            const { rows } = this.props;
            const allIds = rows.map(n => n.id);
            this.setState(({ selected: allIds }));
        } else {
            this.setState({ selected: [] });
        }
    }

    handleClick(event, id) {
        const { selected } = this.state;
        const selectedIndex = selected.indexOf(id);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1),
            );
        }

        this.setState({ selected: newSelected });
    }

    handleChangePage(event, page) {
        this.setState({ page });
    }

    handleChangeRowsPerPage(event) {
        this.setState({ rowsPerPage: event.target.value });
    }

    handleCollapse() {
        const { collapsed } = this.state;
        this.setState({ collapsed: !collapsed });
    }

    handleSelectedAction() {
        const { selected } = this.state;
        const { handleTableAction } = this.props;
        handleTableAction(selected);
    }

    isSelected(id) {
        const { selected } = this.state;
        return selected.indexOf(id) !== -1;
    }

    render() {
        const {
            classes,
            hideToolbar,
            hidePagination,
            tableTitle,
            tableActionText,
            tableActionTooltip,
            tableActionHelpText,
            cols,
            rows: data,
            selectableRows,
            translation,
            collapsible,
            collapseText,
            actionMethods,
        } = this.props;

        const {
            order,
            orderBy,
            selected,
            rowsPerPage,
            page,
            collapsed,
        } = this.state;

        return (
            <>
                {!hideToolbar && (
                    <EnhancedTableToolbar
                        numSelected={selected.length}
                        title={tableTitle}
                        actionText={tableActionText}
                        actionTooltip={tableActionTooltip}
                        handleAction={this.handleSelectedAction}
                        translation={translation}
                        collapsible={collapsible}
                        collapsed={collapsed}
                        collapseText={collapseText}
                        handleCollapse={this.handleCollapse}
                    />
                )}
                <Collapse
                    in={!collapsed}
                    timeout="auto"
                    unmountOnExit
                >
                    <div className={classes.tableWrapper}>
                        {tableActionHelpText && (

                            <Chip
                                label={tableActionHelpText}
                                color="primary"
                                icon={<InfoIcon />}
                                variant="outlined"
                                className={classes.infoChip}
                            />
                        )}
                        <Table className={classes.table} aria-labelledby="tableTitle">
                            <EnhancedTableHead
                                numSelected={selected.length}
                                order={order}
                                orderBy={orderBy}
                                onSelectAllClick={this.handleSelectAllClick}
                                onRequestSort={this.handleRequestSort}
                                rowCount={data.length}
                                cols={cols}
                                selectableRows={selectableRows}
                            />
                            <TableBody
                                className={classes.tableBody}
                            >
                                {stableSort(data, getSorting(order, orderBy))
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((n) => {
                                        const isSelected = this.isSelected(n.id);
                                        return (
                                            <Tooltip
                                                key={n.id}
                                                disableHoverListener={!n.tooltip}
                                                disableTouchListener={!n.tooltip}
                                                disableFocusListener={!n.tooltip}
                                                title={n.tooltip ? n.tooltip : ''}
                                            >
                                                <TableRow
                                                    hover
                                                    onClick={selectableRows
                                                        ? event => this.handleClick(event, n.id)
                                                        : null}
                                                    role="checkbox"
                                                    aria-checked={isSelected}
                                                    tabIndex={-1}
                                                    selected={isSelected}
                                                >
                                                    {selectableRows
                                                    && (
                                                        <TableCell padding="checkbox">
                                                            <Checkbox color="primary" checked={isSelected} />
                                                        </TableCell>
                                                    )
                                                    }
                                                    {
                                                        Object.keys(n).map(key => (key !== 'id' && key !== 'tooltip' ? (
                                                            <TableCell
                                                                key={key}
                                                                align={cols.find(col => col.id === key).numeric ? 'right' : 'left'}
                                                            >
                                                                {typeof n[key] === 'number'
                                                                    ? (
                                                                        n[key].toLocaleString('sv-se')
                                                                    ) : (
                                                                        n[key]
                                                                    )}
                                                                {cols.find(col => col.id === key).link
                                                                    && (
                                                                        <>
                                                                            {n[key] !== '' && (
                                                                                <IconButton
                                                                                    color="primary"
                                                                                    component="a"
                                                                                    target="_blank"
                                                                                    href={n[key]}
                                                                                    onClick={
                                                                                        e => e.stopPropagation()
                                                                                    }
                                                                                >
                                                                                    <PhotoCamera />
                                                                                </IconButton>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                {cols.find(col => col.id === key).action
                                                                    && (
                                                                        <>
                                                                            {n[key] !== '' && (
                                                                                <IconButton
                                                                                    color="primary"
                                                                                    component="button"
                                                                                    onClick={
                                                                                        actionMethods[key](n[key])
                                                                                    }
                                                                                >
                                                                                    <PhotoCamera />
                                                                                </IconButton>
                                                                            )}
                                                                        </>
                                                                    )}
                                                            </TableCell>
                                                        )
                                                            : null
                                                        ))

                                                    }
                                                </TableRow>
                                            </Tooltip>
                                        );
                                    })}
                            </TableBody>
                        </Table>
                    </div>
                    {!hidePagination && (
                        <TablePagination
                            rowsPerPageOptions={perPageOptions}
                            component="div"
                            count={data.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            backIconButtonProps={{
                                'aria-label': 'Previous Page',
                                color: 'primary',
                            }}
                            nextIconButtonProps={{
                                'aria-label': 'Next Page',
                                color: 'primary',
                            }}
                            onChangePage={this.handleChangePage}
                            onChangeRowsPerPage={this.handleChangeRowsPerPage}
                            labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${translation.OF} ${count}`}
                            labelRowsPerPage={`${translation.PER_PAGE}: `}
                        />
                    )}
                </Collapse>
            </>
        );
    }
}

EnhancedTable.propTypes = {
    classes: PropTypes.objectOf(PropTypes.any).isRequired,
    hideToolbar: PropTypes.bool,
    hidePagination: PropTypes.bool,
    translation: translationPropType.isRequired,
    tableTitle: PropTypes.string,
    tableActionText: PropTypes.string,
    tableActionTooltip: PropTypes.string,
    tableActionHelpText: PropTypes.string,
    handleTableAction: PropTypes.func,
    selectableRows: PropTypes.bool,
    collapsible: PropTypes.bool,
    collapseText: PropTypes.string,
    initiallyCollapsed: PropTypes.bool,
    fitInPage: PropTypes.number,
    cols: enhancedTableColsProps.isRequired,
    rows: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    })).isRequired,
    actionMethods: PropTypes.objectOf(PropTypes.any),
};

EnhancedTable.defaultProps = {
    hideToolbar: false,
    hidePagination: false,
    selectableRows: false,
    tableTitle: null,
    tableActionText: '',
    tableActionTooltip: '',
    tableActionHelpText: null,
    handleTableAction: null,
    fitInPage: null,
    collapsible: false,
    initiallyCollapsed: false,
    collapseText: null,
    actionMethods: null,
};

export default withStyles(styles)(EnhancedTable);
